import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useStores } from '../hooks/use-stores';
import { palette } from '@awarego-ui/components';
import { toValidColor } from '@awarego-ui/components/utils/helpers';

const DEFAULT_PRIMARY = palette.evergreen.aspargus;
const DEFAULT_SECONDARY = palette.vibrant.raspberry;
// Must match packages/my/src/css/index.scss and LMS — empty ui.font.family
// used to fall back to system UI and override Nunito on the whole MY app.
const DEFAULT_FONT = "'Nunito Sans', sans-serif";
const DEFAULT_TEXT = '#343534';
const USERWAY_ACCOUNT = 'OODy0J1sGN';

let userwayScriptColor = null;

function hexForUserway(color) {
    const valid = toValidColor(color, DEFAULT_PRIMARY);
    const hexMatch = String(valid)
        .trim()
        .match(/^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (hexMatch) {
        let hex = hexMatch[1];
        if (hex.length === 3) {
            hex = hex
                .split('')
                .map((c) => c + c)
                .join('');
        }
        return hex;
    }
    return DEFAULT_PRIMARY.replace('#', '');
}

function paintUserwayIcon(color) {
    if (!color) return;
    const nodes = document.querySelectorAll(
        '#userwayAccessibilityIcon, .userway_p1, .uai, .uai-trigger, .uwaw-widget-trigger'
    );
    nodes.forEach((el) => {
        el.style.setProperty('background-color', color, 'important');
        el.style.setProperty('background', color, 'important');
    });
}

function loadUserway(hex) {
    if (!hex) return;

    window.userwayOptions = {
        ...(window.userwayOptions || {}),
        account: USERWAY_ACCOUNT,
        color: hex,
        enable_translations: false,
        translations: false,
    };

    if (userwayScriptColor === hex) {
        paintUserwayIcon(`#${hex}`);
        return;
    }

    const existing = document.querySelector(
        'script[src*="cdn.userway.org/widget.js"]'
    );
    if (!existing) {
        const script = document.createElement('script');
        script.src = 'https://cdn.userway.org/widget.js';
        script.async = true;
        script.setAttribute('data-account', USERWAY_ACCOUNT);
        script.setAttribute('data-color', `#${hex}`);
        document.body.appendChild(script);
    }

    userwayScriptColor = hex;
    paintUserwayIcon(`#${hex}`);
}

/**
 * See packages/my/PLAYER_CSS.md for variables, selectors, and the
 * allowlisted properties stored on ui.my.customCss.
 */
function BrandingCss() {
    const { brandingStore } = useStores();
    const location = useLocation();
    const isScormRoute = (location.pathname || '').includes('/cmi5/');
    const {
        brandingLoaded,
        theme,
        fontFamily,
        fontUrl,
        customCss,
        canFastForward,
        hideClose,
    } = brandingStore;

    const primary = theme?.primary || DEFAULT_PRIMARY;
    const secondary = theme?.secondary || DEFAULT_SECONDARY;
    const family = fontFamily || DEFAULT_FONT;
    const userwayColor = hexForUserway(primary);

    const frameworkCss = useMemo(
        () => `
:root {
  --ag-color-primary: ${primary};
  --ag-color-secondary: ${secondary};
  --ag-color-on-primary: #ffffff;
  --ag-color-text: ${DEFAULT_TEXT};
  --ag-color-muted: #767676;
  --ag-color-background: #ffffff;
  --ag-color-surface: #ffffff;
  --ag-color-border: #e8e8e8;
  --ag-font-family: ${family};
}
html, body, #root, .wizard, .ag-player {
  font-family: var(--ag-font-family);
  color: var(--ag-color-text);
}
.wizard,
.ag-player,
.ag-player-body,
.wizard-body,
.ag-player-content,
.wizard-content {
  background-color: var(--ag-color-background);
}
.ag-player-header,
.ag-player-sidebar,
.sidebar,
.ag-player-footer,
.wizard-footer,
.ag-player-steps,
.block-steps {
  background-color: var(--ag-color-surface);
}
.ag-player-progress-bar {
  background-color: var(--ag-color-primary) !important;
}
.wizard .ag-player-btn,
.ag-player .ag-player-btn {
  background-color: var(--ag-color-primary) !important;
  border-color: var(--ag-color-primary) !important;
  color: var(--ag-color-on-primary) !important;
}
.wizard .ag-player-btn:hover,
.wizard .ag-player-btn:focus,
.ag-player .ag-player-btn:hover,
.ag-player .ag-player-btn:focus {
  background-color: color-mix(in srgb, var(--ag-color-primary) 85%, #000) !important;
  border-color: color-mix(in srgb, var(--ag-color-primary) 85%, #000) !important;
  color: var(--ag-color-on-primary) !important;
}
.ag-player-btn-prev,
.ag-player-back,
.sidebar .btn-link,
.ag-player-footer .btn-link {
  color: var(--ag-color-primary);
}
.ag-player-step-active {
  border-left-color: var(--ag-color-primary) !important;
  border-right-color: var(--ag-color-primary) !important;
}
.ag-player-step-active a {
  color: var(--ag-color-primary) !important;
}
.ag-player-step-enabled:hover {
  color: var(--ag-color-primary);
}
.ag-player-answer:not(.ag-player-answer-correct):not(.ag-player-answer-incorrect) {
  background-color: color-mix(in srgb, var(--ag-color-primary) 10%, transparent) !important;
  color: var(--ag-color-primary) !important;
}
.ag-player-answer:not(.ag-player-answer-correct):not(.ag-player-answer-incorrect):hover:not(:disabled) {
  background-color: var(--ag-color-primary) !important;
  color: var(--ag-color-on-primary) !important;
}
.wizard input:checked ~ .checkmark {
  background: var(--ag-color-primary) !important;
  border-color: var(--ag-color-primary) !important;
}
.ag-hide-player-close .ag-player-close {
  display: none !important;
}
.ag-no-fast-forward .jw-slider-time,
.ag-no-fast-forward .jw-slider-time * {
  pointer-events: none !important;
}
#userwayAccessibilityIcon,
.userway_p1,
.uai,
.uai-trigger,
.uwaw-widget-trigger {
  background-color: var(--ag-color-primary) !important;
  background: var(--ag-color-primary) !important;
}
.ag-language-select .MuiInputLabel-root.Mui-focused {
  color: var(--ag-color-primary) !important;
}
.ag-language-select .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline,
.ag-language-select .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
  border-color: var(--ag-color-primary) !important;
}
.ag-language-select-menu .MuiMenuItem-root.Mui-selected,
.ag-language-select-menu .MuiMenuItem-root.Mui-selected:hover {
  background-color: color-mix(in srgb, var(--ag-color-primary) 16%, transparent) !important;
}
.ag-language-select-menu .MuiMenuItem-root.Mui-focusVisible {
  outline: 2px solid var(--ag-color-primary);
  outline-offset: -2px;
}
`,
        [primary, secondary, family]
    );

    useEffect(() => {
        if (!brandingLoaded) return;
        const root = document.documentElement;
        root.classList.toggle(
            'ag-hide-player-close',
            !!hideClose || isScormRoute
        );
        root.classList.toggle('ag-no-fast-forward', !canFastForward);
        loadUserway(userwayColor);
        const onUserwayRender = () => paintUserwayIcon(`#${userwayColor}`);
        document.addEventListener('userway:render_completed', onUserwayRender);
        return () => {
            root.classList.remove('ag-hide-player-close');
            root.classList.remove('ag-no-fast-forward');
            document.removeEventListener(
                'userway:render_completed',
                onUserwayRender
            );
        };
    }, [brandingLoaded, hideClose, canFastForward, userwayColor, isScormRoute]);

    if (!brandingLoaded) return null;

    return (
        <Helmet>
            {fontUrl ? <link rel="stylesheet" href={fontUrl} /> : null}
            <style type="text/css">{frameworkCss}</style>
            {customCss ? <style type="text/css">{customCss}</style> : null}
        </Helmet>
    );
}

export default observer(BrandingCss);
